import React, { useState } from 'react';
import { X, Plus, Trash2, Calendar, Package, AlertCircle } from 'lucide-react';
import { LocalItem, ItemExpiry, itemService } from '../services/itemService';
import { useAuth } from '../utils/AuthContext';
import { supabase } from '../utils/supabase';

interface ItemShelfExpiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: LocalItem;
  onUpdate: (updatedItem: LocalItem) => void;
}

interface ItemShelf {
  id: string;
  shelf_name: string;
}

const ItemShelfExpiryModal: React.FC<ItemShelfExpiryModalProps> = ({
  isOpen,
  onClose,
  item,
  onUpdate
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Shelf state
  const [newShelfName, setNewShelfName] = useState('');
  const [shelfLoading, setShelfLoading] = useState(false);
  
  // Expiry state
  const [newExpiry, setNewExpiry] = useState({
    expiry_date: '',
    reminder_days: 7,
    batch_number: '',
    quantity: 0,
    notes: ''
  });
  const [expiryLoading, setExpiryLoading] = useState(false);
  
  // Error state
  const [error, setError] = useState<string | null>(null);

  // Use shelves array directly - now they already include IDs
  const shelves = item.shelves || [];

  const expiries: ItemExpiry[] = item.expiries || [];

  const refreshItemData = async () => {
    try {
      // Fetch fresh item data from the database by searching for the item
      const { data, error } = await supabase.rpc('get_items_with_images_and_stock', {
        p_store_id: null,
        p_search_term: item.name,
        p_page: 1,
        p_limit: 5,
        p_stock_filter_enabled: false,
        p_stock_operator: 'gt',
        p_stock_value: 0
      });

      if (error) {
        console.error('Error fetching fresh item data:', error);
        return item;
      }

      if (data && data.success && data.data && data.data.length > 0) {
        // Find the exact item by ID
        const freshItem = data.data.find((i: any) => i.id === item.id);
        if (freshItem) {
          onUpdate(freshItem);
          return freshItem;
        }
      }
    } catch (error) {
      console.error('Error refreshing item data:', error);
    }
    return item; // fallback to current item
  };

  const handleAddShelf = async () => {
    if (!newShelfName.trim()) return;
    
    setShelfLoading(true);
    setError(null); // Clear any previous errors
    try {
      const result = await itemService.addShelf(item.id, newShelfName.trim(), user?.id);
      
      if (result.success) {
        // Refresh item data from database to get the latest state
        await refreshItemData();
        setNewShelfName('');
      } else {
        setError(result.message || 'Failed to add shelf');
      }
    } catch (error) {
      console.error('Error adding shelf:', error);
      setError('Failed to add shelf');
    } finally {
      setShelfLoading(false);
    }
  };

  const handleRemoveShelf = async (shelfId: string) => {
    setShelfLoading(true);
    setError(null); // Clear any previous errors
    try {
      const result = await itemService.removeShelf(shelfId, user?.id);
      
      if (result.success) {
        // Refresh item data from database to get the latest state
        await refreshItemData();
      } else {
        setError(result.message || 'Failed to remove shelf');
      }
    } catch (error) {
      console.error('Error removing shelf:', error);
      setError('Failed to remove shelf');
    } finally {
      setShelfLoading(false);
    }
  };

  const handleAddExpiry = async () => {
    if (!newExpiry.expiry_date) return;
    
    setExpiryLoading(true);
    setError(null); // Clear any previous errors
    try {
      const result = await itemService.addExpiry(item.id, {
        expiry_date: newExpiry.expiry_date,
        reminder_days: newExpiry.reminder_days || undefined,
        batch_number: newExpiry.batch_number || undefined,
        quantity: newExpiry.quantity || undefined,
        notes: newExpiry.notes || undefined
      }, user?.id);
      
      if (result.success) {
        // Refresh item data from database to get the latest state
        await refreshItemData();
        
        // Reset form
        setNewExpiry({
          expiry_date: '',
          reminder_days: 7,
          batch_number: '',
          quantity: 0,
          notes: ''
        });
      } else {
        setError(result.message || 'Failed to add expiry');
      }
    } catch (error) {
      console.error('Error adding expiry:', error);
      setError('Failed to add expiry');
    } finally {
      setExpiryLoading(false);
    }
  };

  const handleRemoveExpiry = async (expiryId: string) => {
    setLoading(true);
    setError(null); // Clear any previous errors
    try {
      const result = await itemService.removeExpiry(expiryId, user?.id);
      
      if (result.success) {
        // Refresh item data from database to get the latest state
        await refreshItemData();
      } else {
        setError(result.message || 'Failed to remove expiry');
      }
    } catch (error) {
      console.error('Error removing expiry:', error);
      setError('Failed to remove expiry');
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilExpiry = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryStatus = (expiryDate: string, reminderDays?: number) => {
    const days = getDaysUntilExpiry(expiryDate);
    const reminderThreshold = reminderDays || 7;
    
    if (days < 0) return { status: 'expired', color: 'text-red-700 bg-red-100' };
    if (days <= reminderThreshold) return { status: 'warning', color: 'text-orange-700 bg-orange-100' };
    return { status: 'good', color: 'text-green-700 bg-green-100' };
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manage Shelves & Expiries</h2>
            <p className="text-sm text-gray-600 mt-1">{item.name} (SKU: {item.sku})</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-700 font-medium">Error</span>
              </div>
              <p className="text-red-600 text-sm mt-1">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-600 text-sm underline mt-2 hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Shelves Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-medium text-gray-900">Shelf Locations</h3>
            </div>

            {/* Current Shelves */}
            <div className="space-y-2 mb-4">
              {shelves.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No shelf locations assigned</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {shelves.map((shelf) => (
                    <div key={shelf.id} className="flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-2 rounded-full">
                      <span className="font-medium">{shelf.shelf_name}</span>
                      <button
                        onClick={() => handleRemoveShelf(shelf.id)}
                        disabled={shelfLoading}
                        className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Shelf */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newShelfName}
                onChange={(e) => setNewShelfName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddShelf()}
                placeholder="Enter shelf name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={handleAddShelf}
                disabled={shelfLoading || !newShelfName.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
          </div>

          {/* Expiries Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-medium text-gray-900">Expiry Dates</h3>
            </div>

            {/* Current Expiries */}
            <div className="space-y-3 mb-6">
              {expiries.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No expiry dates added</p>
              ) : (
                expiries.map((expiry) => {
                  const expiryStatus = getExpiryStatus(expiry.expiry_date, expiry.reminder_days);
                  const daysUntil = getDaysUntilExpiry(expiry.expiry_date);
                  
                  return (
                    <div key={expiry.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium">
                              {new Date(expiry.expiry_date).toLocaleDateString()}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${expiryStatus.color}`}>
                              {daysUntil < 0 ? 'Expired' : daysUntil === 0 ? 'Today' : `${daysUntil} days`}
                            </span>
                            {daysUntil <= (expiry.reminder_days || 7) && daysUntil >= 0 && (
                              <AlertCircle className="w-4 h-4 text-orange-500" />
                            )}
                          </div>
                          
                          <div className="text-sm text-gray-600 space-y-1">
                            {expiry.batch_number && (
                              <div><span className="font-medium">Batch:</span> {expiry.batch_number}</div>
                            )}
                            {expiry.quantity && (
                              <div><span className="font-medium">Quantity:</span> {expiry.quantity} units</div>
                            )}
                            {expiry.reminder_days && (
                              <div><span className="font-medium">Reminder:</span> {expiry.reminder_days} days before</div>
                            )}
                            {expiry.notes && (
                              <div><span className="font-medium">Notes:</span> {expiry.notes}</div>
                            )}
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleRemoveExpiry(expiry.id)}
                          disabled={loading}
                          className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Add New Expiry */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium mb-3">Add New Expiry Date</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Date *
                  </label>
                  <input
                    type="date"
                    value={newExpiry.expiry_date}
                    onChange={(e) => setNewExpiry({ ...newExpiry, expiry_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reminder (days before)
                  </label>
                  <input
                    type="number"
                    value={newExpiry.reminder_days}
                    onChange={(e) => setNewExpiry({ ...newExpiry, reminder_days: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Batch Number
                  </label>
                  <input
                    type="text"
                    value={newExpiry.batch_number}
                    onChange={(e) => setNewExpiry({ ...newExpiry, batch_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={newExpiry.quantity}
                    onChange={(e) => setNewExpiry({ ...newExpiry, quantity: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={newExpiry.notes}
                    onChange={(e) => setNewExpiry({ ...newExpiry, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <button
                  onClick={handleAddExpiry}
                  disabled={expiryLoading || !newExpiry.expiry_date}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Expiry
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemShelfExpiryModal;
